import {
  getCurrentFacilityId,
  userLoggedInFacilityType,
} from "@risalabs_frontend_org/auth-wrapper";
import React from "react";
import {
  AuthOnFile,
  TestLocationSubmissionDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { isExceptionFlowForRadiologyOrg } from "../../../../../enums/organizationTypes";
import { useFacilityCategory } from "../../../../../hooks/useOncoemrTasksEnabled";
import useDxCodeMapRefresh from "../../../hooks/useDxCodeMapRefresh";
import PrimaryIcdCode from "./PrimaryIcdCode";
import SiteNpiDetails from "./SiteNpiDetails";
/** Props for the order header authorization details strip. */
interface AuthorizationDetailsProps {
  /** Primary payer / insurance name. */
  insuranceName?: string;
  /** Date of service for the order. */
  dateOfService?: string;
  /** Rendering provider name. */
  providerName?: string;
  /** Servicing facility / location label. */
  location?: string;
  /** Primary ICD-10 code(s); arrays are joined for display. */
  primaryIcdCode?: string | string[];
  /** Medical PA order ID. */
  orderId: string;
  /** Invoked when the edit (pencil) affordance for the ICD code is clicked. */
  onEditIcdClick?: () => void;
  /** Invoked when the edit (pencil) affordance for site NPI is clicked. */
  onEditSiteNpiClick?: () => void;
  /** Site NPI details. */
  siteNpiDetails?: TestLocationSubmissionDetails;
  /** Auth on file details. */
  authOnFileDetails?: AuthOnFile;
}

/**
 * Renders the single-order header strip with insurance, DOS, provider,
 * location, and the primary ICD-10 code segment.
 *
 * Site NPI details are included only for radiology facilities (via
 * {@link isExceptionFlowForRadiologyOrg} on facility `category`).
 *
 * @param props - Authorization detail fields and ICD / site NPI edit handlers.
 * @returns The authorization details header row.
 */
export const AuthorizationDetails: React.FC<AuthorizationDetailsProps> = ({
  insuranceName,
  dateOfService,
  providerName,
  location,
  primaryIcdCode = "NA",
  orderId,
  onEditIcdClick,
  onEditSiteNpiClick,
  siteNpiDetails,
  authOnFileDetails,
}) => {
  const { isRefreshing } = useDxCodeMapRefresh();
  const formattedIcdCode = Array.isArray(primaryIcdCode)
    ? primaryIcdCode.join(", ") || "NA"
    : primaryIcdCode;
  const orgId = getCurrentFacilityId() || "";
  const isExternalFacility = userLoggedInFacilityType(orgId) === "external";
  const facilityCategory = useFacilityCategory();
  const isRadiologyFacility = isExceptionFlowForRadiologyOrg(facilityCategory);

  return (
    <div className="flex flex-row items-center gap-2">
      <div className="pl-2 text-sm">{`Insurance: ${insuranceName}`}</div>
      <div className="border-l border-primaryGray-14 pl-2 text-sm">{`DOS: ${dateOfService}`}</div>
      <div className="border-l border-primaryGray-14 pl-2 text-sm">{`Provider: ${providerName}`}</div>
      <div className="border-l border-primaryGray-14 pl-2 text-sm">{`Location: ${location}`}</div>
      <PrimaryIcdCode
        formattedIcdCode={formattedIcdCode}
        isRefreshing={isRefreshing}
        isExternalFacility={isExternalFacility}
        onEditIcdClick={onEditIcdClick}
      />
      {isRadiologyFacility && (
        <SiteNpiDetails
          onEditSiteNpiClick={onEditSiteNpiClick}
          siteNpiDetails={siteNpiDetails}
          authOnFileDetails={authOnFileDetails}
        />
      )}
    </div>
  );
};
export default AuthorizationDetails;
